<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Modal from 'dashboard/components/Modal.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  webhook: {
    type: Object,
    default: null,
  },
  pipelines: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'saved']);
const store = useStore();
const { showAlert } = useAlert();

const { t } = useI18n();
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);
let accountGeneration = 0;
let saveGeneration = 0;
let regenerateGeneration = 0;
let closeEmitted = false;
const emitClose = () => {
  if (closeEmitted) return;
  closeEmitted = true;
  emit('close');
};
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const openingAccountId = normalizedAccountId();
const modalAccountActive = computed(
  () => openingAccountId !== null && openingAccountId === normalizedAccountId()
);
const captureAccountContext = () => ({
  accountId: openingAccountId,
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  modalAccountActive.value &&
  context.accountId === openingAccountId &&
  context.generation === accountGeneration;

const isEditing = computed(() => !!props.webhook?.id);
const isSaving = ref(false);
const isRegenerating = ref(false);
const showSecret = ref(false);
const displaySecret = ref('');
const initialFormSnapshot = ref('');
const discardConfirmation = ref(null);
const regenerateConfirmation = ref(null);
const nameInputRef = ref(null);
const urlInputRef = ref(null);
const eventsGroupRef = ref(null);
const validationAttempted = ref(false);

const EVENT_CONFIG = [
  {
    value: 'pipeline_card_created',
    icon: 'i-lucide-plus-circle',
    color: 'text-success',
  },
  {
    value: 'pipeline_card_updated',
    icon: 'i-lucide-pencil-line',
    color: 'text-n-blue-11',
  },
  {
    value: 'pipeline_card_deleted',
    icon: 'i-lucide-trash-2',
    color: 'text-danger',
  },
  {
    value: 'pipeline_card_stage_changed',
    icon: 'i-lucide-arrow-right-circle',
    color: 'text-n-yellow-11',
  },
  {
    value: 'pipeline_card_won',
    icon: 'i-lucide-trophy',
    color: 'text-success',
  },
  {
    value: 'pipeline_card_lost',
    icon: 'i-lucide-circle-x',
    color: 'text-danger',
  },
  {
    value: 'pipeline_card_owner_changed',
    icon: 'i-lucide-user-round-cog',
    color: 'text-n-purple-11',
  },
  // Único evento que nasce de um relógio, não de uma ação: ninguém está
  // olhando a tela quando ele acontece — por isso sair para fora importa.
  {
    value: 'pipeline_card_sla_exceeded',
    icon: 'i-lucide-alarm-clock',
    color: 'text-n-amber-11',
  },
];

const availableEvents = computed(() =>
  EVENT_CONFIG.map(e => ({
    ...e,
    label: t(`PIPELINE_WEBHOOK.FORM.EVENTS.${e.value}.LABEL`),
    description: t(`PIPELINE_WEBHOOK.FORM.EVENTS.${e.value}.DESCRIPTION`),
  }))
);

const form = ref({
  name: '',
  url: '',
  pipeline_id: '',
  events: [],
  active: true,
});

const wipeAccountOwnedState = () => {
  form.value = {
    name: '',
    url: '',
    pipeline_id: '',
    events: [],
    active: true,
  };
  displaySecret.value = '';
  showSecret.value = false;
  initialFormSnapshot.value = JSON.stringify(form.value);
  validationAttempted.value = false;
};

watch(
  () => props.webhook,
  webhook => {
    if (!modalAccountActive.value) {
      wipeAccountOwnedState();
      return;
    }
    if (webhook) {
      form.value = {
        name: webhook.name || '',
        url: webhook.url || '',
        pipeline_id: webhook.pipeline_id || '',
        events: [...(webhook.events || [])],
        active: webhook.active !== false,
      };
      displaySecret.value = webhook.secret || '';
    } else {
      form.value = {
        name: '',
        url: '',
        pipeline_id: '',
        events: [],
        active: true,
      };
      displaySecret.value = '';
    }
    initialFormSnapshot.value = JSON.stringify(form.value);
    showSecret.value = false;
    validationAttempted.value = false;
  },
  { immediate: true }
);

const parseHttpUrl = value => {
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol) && parsed.hostname
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const isPrivateIpv4 = hostname => {
  const octets = hostname.split('.').map(Number);
  if (
    octets.length !== 4 ||
    octets.some(octet => !Number.isInteger(octet) || octet < 0 || octet > 255)
  ) {
    return false;
  }

  const [first, second, third] = octets;
  return (
    first === 0 ||
    first === 10 ||
    first === 127 ||
    (first === 100 && second >= 64 && second <= 127) ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 0 && third === 0) ||
    (first === 192 && second === 168) ||
    (first === 198 && (second === 18 || second === 19)) ||
    first >= 224
  );
};

const isPrivateHostname = rawHostname => {
  const hostname = rawHostname
    .toLowerCase()
    .replace(/^\[/, '')
    .replace(/\]$/, '');

  if (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.internal')
  ) {
    return true;
  }

  if (!hostname.includes('.') && !hostname.includes(':')) return true;

  if (isPrivateIpv4(hostname)) return true;
  if (!hostname.includes(':')) return false;

  return (
    hostname === '::' ||
    hostname === '::1' ||
    hostname.startsWith('::ffff:') ||
    /^(fc|fd)/.test(hostname) ||
    /^fe[89ab]/.test(hostname) ||
    hostname.startsWith('ff')
  );
};

const urlValidationError = computed(() => {
  const value = form.value.url.trim();
  if (!value) return null;

  const parsed = parseHttpUrl(value);
  if (!parsed) return 'PIPELINE_WEBHOOK.FORM.URL_INVALID';
  return isPrivateHostname(parsed.hostname)
    ? 'PIPELINE_WEBHOOK.FORM.URL_PUBLIC_REQUIRED'
    : null;
});

const urlValidationErrorMessage = computed(() => {
  if (urlValidationError.value === 'PIPELINE_WEBHOOK.FORM.URL_INVALID') {
    return t('PIPELINE_WEBHOOK.FORM.URL_INVALID');
  }
  if (
    urlValidationError.value === 'PIPELINE_WEBHOOK.FORM.URL_PUBLIC_REQUIRED'
  ) {
    return t('PIPELINE_WEBHOOK.FORM.URL_PUBLIC_REQUIRED');
  }
  return '';
});

const isUrlValid = computed(() => !urlValidationError.value);

const isFormValid = computed(() => {
  return (
    form.value.name.trim() &&
    form.value.url.trim() &&
    isUrlValid.value &&
    form.value.events.length > 0
  );
});

const allEventsSelected = computed(
  () => form.value.events.length === availableEvents.value.length
);

const isDirty = computed(
  () => JSON.stringify(form.value) !== initialFormSnapshot.value
);

const pipelineOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE_WEBHOOK.FORM.ALL_PIPELINES'),
  },
  ...Array.from(props.pipelines || []).map(pipeline => ({
    value: pipeline.id,
    label: pipeline.name,
  })),
]);

const toggleAllEvents = () => {
  if (isSaving.value) return;
  if (allEventsSelected.value) {
    form.value.events = [];
  } else {
    form.value.events = availableEvents.value.map(e => e.value);
  }
};

const handleSave = async () => {
  if (isSaving.value || !modalAccountActive.value) return;
  validationAttempted.value = true;
  if (!isFormValid.value) {
    await nextTick();
    if (!form.value.name.trim()) nameInputRef.value?.focus();
    else if (!form.value.url.trim() || !isUrlValid.value) {
      urlInputRef.value?.focus();
    } else {
      eventsGroupRef.value?.focus();
    }
    return;
  }
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  const webhookId = props.webhook?.id;
  saveGeneration += 1;
  const requestGeneration = saveGeneration;
  const requestIsCurrent = () =>
    requestGeneration === saveGeneration &&
    accountContextIsCurrent(accountContext);
  isSaving.value = true;
  try {
    const payload = {
      pipeline_webhook: {
        ...form.value,
        name: form.value.name.trim(),
        url: form.value.url.trim(),
        pipeline_id: form.value.pipeline_id || null,
        events: [...new Set(form.value.events)],
      },
    };
    if (isEditing.value) {
      await store.dispatch('pipelineWebhooks/update', {
        id: webhookId,
        accountId: accountContext.accountId,
        ...payload,
      });
    } else {
      await store.dispatch('pipelineWebhooks/create', {
        accountId: accountContext.accountId,
        ...payload,
      });
    }
    if (!requestIsCurrent()) return;
    emit('saved');
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SAVE_SUCCESS'));
  } catch {
    if (!requestIsCurrent()) return;
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SAVE_ERROR'));
  } finally {
    if (requestIsCurrent()) isSaving.value = false;
  }
};

const copySecret = async () => {
  const accountContext = captureAccountContext();
  if (!accountContextIsCurrent(accountContext) || !displaySecret.value) return;
  const secret = displaySecret.value;
  try {
    await navigator.clipboard.writeText(secret);
    if (!accountContextIsCurrent(accountContext)) return;
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SECRET_COPIED'));
  } catch {
    if (!accountContextIsCurrent(accountContext)) return;
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SECRET_COPY_ERROR'));
  }
};

const handleRegenerateSecret = async () => {
  if (isRegenerating.value || !modalAccountActive.value) return;
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  const webhookId = props.webhook?.id;
  if (!webhookId) return;
  regenerateGeneration += 1;
  const requestGeneration = regenerateGeneration;
  const requestIsCurrent = () =>
    requestGeneration === regenerateGeneration &&
    accountContextIsCurrent(accountContext);
  const confirmed = await regenerateConfirmation.value.showConfirmation();
  if (!confirmed || !requestIsCurrent()) return;

  isRegenerating.value = true;
  try {
    const updatedWebhook = await store.dispatch(
      'pipelineWebhooks/regenerateSecret',
      {
        id: webhookId,
        accountId: accountContext.accountId,
      }
    );
    if (!requestIsCurrent()) return;
    displaySecret.value = updatedWebhook?.secret || displaySecret.value;
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_SUCCESS'));
  } catch {
    if (!requestIsCurrent()) return;
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_ERROR'));
  } finally {
    if (requestIsCurrent()) isRegenerating.value = false;
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    saveGeneration += 1;
    regenerateGeneration += 1;
    isSaving.value = false;
    isRegenerating.value = false;
    wipeAccountOwnedState();
    emitClose();
  },
  { flush: 'sync' }
);

const handleClose = async () => {
  const accountContext = captureAccountContext();
  if (!accountContextIsCurrent(accountContext)) return;
  if (isSaving.value || isRegenerating.value) return;
  if (!isDirty.value) {
    emitClose();
    return;
  }

  const confirmed = await discardConfirmation.value.showConfirmation();
  if (confirmed && accountContextIsCurrent(accountContext)) emitClose();
};
</script>

<template>
  <Modal
    show
    size="medium"
    aria-labelledby="pipeline-webhook-modal-title"
    :close-on-escape="!isSaving && !isRegenerating"
    :close-on-backdrop-click="!isSaving && !isRegenerating"
    @close="handleClose"
  >
    <form
      class="mx-auto flex max-h-[90dvh] w-full max-w-2xl flex-col"
      @submit.prevent="handleSave"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between border-b border-n-weak px-5 pb-4 pr-14 pt-5"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-lg bg-n-brand/10 flex items-center justify-center shrink-0"
          >
            <Icon icon="i-lucide-webhook" class="size-4 text-n-brand" />
          </div>
          <div>
            <h3
              id="pipeline-webhook-modal-title"
              class="text-sm font-semibold text-n-slate-12 leading-none"
            >
              {{
                isEditing
                  ? $t('PIPELINE_WEBHOOK.MODAL.EDIT_TITLE')
                  : $t('PIPELINE_WEBHOOK.MODAL.CREATE_TITLE')
              }}
            </h3>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ $t('PIPELINE_WEBHOOK.DESCRIPTION') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Form -->
      <div class="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
        <!-- Name -->
        <div>
          <label
            for="pipeline-webhook-name"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.NAME_LABEL') }}
          </label>
          <div
            class="flex items-center rounded-lg border border-n-weak bg-n-background focus-within:ring-2 focus-within:ring-n-brand/30 focus-within:border-n-brand transition-colors"
          >
            <Icon
              icon="i-lucide-tag"
              class="ml-3 size-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <input
              id="pipeline-webhook-name"
              ref="nameInputRef"
              v-model="form.name"
              name="pipelineWebhookName"
              type="text"
              required
              :aria-invalid="
                validationAttempted && !form.name.trim() ? 'true' : undefined
              "
              :aria-describedby="
                validationAttempted && !form.name.trim()
                  ? 'pipeline-webhook-name-error'
                  : undefined
              "
              class="reset-base min-h-11 flex-1 pl-2 pr-3 py-2 bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-9 border-none outline-none"
              :placeholder="$t('PIPELINE_WEBHOOK.FORM.NAME_PLACEHOLDER')"
            />
          </div>
          <p
            v-if="validationAttempted && !form.name.trim()"
            id="pipeline-webhook-name-error"
            role="alert"
            class="mt-1 text-xs text-n-ruby-11"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.NAME_REQUIRED') }}
          </p>
        </div>

        <!-- URL -->
        <div>
          <label
            for="pipeline-webhook-url"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.URL_LABEL') }}
          </label>
          <div
            class="flex items-center rounded-lg border border-n-weak bg-n-background focus-within:ring-2 focus-within:ring-n-brand/30 focus-within:border-n-brand transition-colors"
          >
            <Icon
              icon="i-lucide-link"
              class="ml-3 size-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <input
              id="pipeline-webhook-url"
              ref="urlInputRef"
              v-model="form.url"
              name="pipelineWebhookUrl"
              type="url"
              required
              :aria-invalid="!isUrlValid"
              :aria-describedby="
                !isUrlValid ? 'pipeline-webhook-url-error' : undefined
              "
              class="reset-base min-h-11 flex-1 pl-2 pr-3 py-2 bg-transparent text-sm text-n-slate-12 font-mono placeholder:text-n-slate-9 placeholder:font-sans border-none outline-none"
              :placeholder="$t('PIPELINE_WEBHOOK.FORM.URL_PLACEHOLDER')"
            />
          </div>
          <p
            v-if="form.url.trim() && !isUrlValid"
            id="pipeline-webhook-url-error"
            role="alert"
            class="mt-1 text-xs text-n-ruby-11"
          >
            {{ urlValidationErrorMessage }}
          </p>
        </div>

        <!-- Pipeline Scope -->
        <div>
          <label
            for="pipeline-webhook-scope"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.PIPELINE_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-globe"
              class="size-4 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <Select
              id="pipeline-webhook-scope-select"
              v-model="form.pipeline_id"
              input-id="pipeline-webhook-scope"
              name="pipelineWebhookScope"
              class="min-w-0 flex-1 [&>select]:min-h-11 [&>select]:w-full"
              :aria-label="$t('PIPELINE_WEBHOOK.FORM.PIPELINE_LABEL')"
              :options="pipelineOptions"
            />
          </div>
          <p class="text-xs text-n-slate-10 mt-1.5">
            {{ $t('PIPELINE_WEBHOOK.FORM.PIPELINE_HINT') }}
          </p>
        </div>

        <!-- Events -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <span
              id="pipeline-webhook-events-label"
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('PIPELINE_WEBHOOK.FORM.EVENTS_LABEL') }}
              <span
                v-if="form.events.length"
                class="ml-1 px-1.5 py-px rounded-md bg-n-brand/10 text-n-brand normal-case tracking-normal font-medium"
              >
                {{
                  $t('PIPELINE_WEBHOOK.FORM.EVENTS_COUNT', {
                    selected: form.events.length,
                    total: availableEvents.length,
                  })
                }}
              </span>
            </span>
            <button
              type="button"
              class="min-h-11 rounded-lg px-2 text-xs text-n-blue-11 hover:text-n-blue-12 hover:bg-n-alpha-1 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isSaving"
              @click="toggleAllEvents"
            >
              {{
                allEventsSelected
                  ? $t('PIPELINE_WEBHOOK.FORM.DESELECT_ALL')
                  : $t('PIPELINE_WEBHOOK.FORM.SELECT_ALL')
              }}
            </button>
          </div>
          <div
            ref="eventsGroupRef"
            role="group"
            tabindex="-1"
            aria-labelledby="pipeline-webhook-events-label"
            :aria-invalid="
              validationAttempted && !form.events.length ? 'true' : undefined
            "
            :aria-describedby="
              validationAttempted && !form.events.length
                ? 'pipeline-webhook-events-error'
                : undefined
            "
            class="grid grid-cols-1 gap-1.5 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          >
            <label
              v-for="event in availableEvents"
              :key="event.value"
              class="flex min-h-11 items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-[background-color,border-color] duration-100 focus-within:outline focus-within:outline-2 focus-within:outline-n-brand"
              :class="
                form.events.includes(event.value)
                  ? 'border-n-brand/40 bg-n-brand/5'
                  : 'border-n-weak hover:border-n-strong hover:bg-n-alpha-1'
              "
            >
              <div
                class="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                :class="
                  form.events.includes(event.value)
                    ? 'bg-n-brand border-n-brand'
                    : 'border-n-slate-8 bg-n-background'
                "
              >
                <Icon
                  v-if="form.events.includes(event.value)"
                  icon="i-lucide-check"
                  class="size-2.5 text-white"
                />
              </div>
              <input
                v-model="form.events"
                type="checkbox"
                name="pipelineWebhookEvents"
                :value="event.value"
                :disabled="isSaving"
                :aria-label="event.label"
                class="sr-only"
              />
              <Icon
                :icon="event.icon"
                class="size-4 shrink-0"
                :class="event.color"
              />
              <div class="min-w-0">
                <span
                  class="text-sm font-medium text-n-slate-12 block leading-tight"
                >
                  {{ event.label }}
                </span>
                <p class="text-xs text-n-slate-10 leading-tight mt-px">
                  {{ event.description }}
                </p>
              </div>
            </label>
          </div>
          <p
            v-if="validationAttempted && !form.events.length"
            id="pipeline-webhook-events-error"
            role="alert"
            class="mt-1 text-xs text-n-ruby-11"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.EVENTS_REQUIRED') }}
          </p>
        </div>

        <!-- Active Toggle -->
        <label
          class="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-3"
        >
          <div class="flex items-center gap-2.5">
            <Icon
              icon="i-lucide-power"
              class="size-4 text-n-slate-11 shrink-0"
            />
            <div>
              <span
                class="text-sm font-medium text-n-slate-12 block leading-tight"
              >
                {{ $t('PIPELINE_WEBHOOK.FORM.ACTIVE_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-10 mt-px">
                {{ $t('PIPELINE_WEBHOOK.FORM.ACTIVE_HINT') }}
              </p>
            </div>
          </div>
          <span class="relative inline-flex h-5 w-9 shrink-0">
            <input
              v-model="form.active"
              type="checkbox"
              name="pipelineWebhookActive"
              :disabled="isSaving"
              :aria-label="$t('PIPELINE_WEBHOOK.FORM.ACTIVE_LABEL')"
              class="sr-only peer"
            />
            <span
              class="absolute inset-0 rounded-full bg-n-slate-7 transition-colors peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand peer-disabled:opacity-50"
            />
            <span
              class="absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform duration-200 peer-checked:translate-x-4"
            />
          </span>
        </label>

        <!-- Secret (only show when editing) -->
        <div v-if="isEditing && displaySecret">
          <span
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.SECRET_LABEL') }}
          </span>
          <div
            class="rounded-lg border border-n-weak bg-n-alpha-1 overflow-hidden"
          >
            <div class="flex items-center gap-0">
              <div class="flex items-center gap-2 flex-1 px-3 py-2 min-w-0">
                <Icon
                  icon="i-lucide-key-round"
                  class="size-3.5 text-n-slate-10 shrink-0"
                />
                <span
                  class="text-xs font-mono text-n-slate-11 truncate"
                  :class="showSecret ? '' : 'tracking-widest'"
                >
                  {{ showSecret ? displaySecret : '\u2022'.repeat(24) }}
                </span>
              </div>
              <div class="flex items-center border-l border-n-weak">
                <button
                  type="button"
                  class="flex size-11 items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                  :aria-label="
                    showSecret
                      ? $t('PIPELINE_WEBHOOK.FORM.HIDE')
                      : $t('PIPELINE_WEBHOOK.FORM.SHOW')
                  "
                  :disabled="isRegenerating || !modalAccountActive"
                  @click="showSecret = !showSecret"
                >
                  <Icon
                    :icon="showSecret ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    class="size-3.5"
                  />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                  :aria-label="$t('PIPELINE_WEBHOOK.FORM.COPY_SECRET')"
                  :disabled="isRegenerating || !modalAccountActive"
                  @click="copySecret"
                >
                  <Icon icon="i-lucide-copy" class="size-3.5" />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                  :aria-label="$t('PIPELINE_WEBHOOK.FORM.REGENERATE_SECRET')"
                  :disabled="isRegenerating || isSaving || !modalAccountActive"
                  @click="handleRegenerateSecret"
                >
                  <Icon
                    icon="i-lucide-rotate-cw"
                    class="size-3.5"
                    :class="{ 'motion-safe:animate-spin': isRegenerating }"
                  />
                </button>
              </div>
            </div>
          </div>
          <p class="text-xs text-n-slate-10 mt-1.5 flex items-center gap-1">
            <Icon icon="i-lucide-shield-check" class="size-3 shrink-0" />
            {{ $t('PIPELINE_WEBHOOK.FORM.SECRET_HINT') }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex flex-col-reverse items-stretch justify-end gap-2 border-t border-n-weak px-5 py-4 sm:flex-row"
      >
        <Button
          type="button"
          variant="ghost"
          size="md"
          :disabled="isSaving || isRegenerating || !modalAccountActive"
          @click="handleClose"
        >
          {{ $t('PIPELINE_WEBHOOK.FORM.CANCEL') }}
        </Button>
        <Button
          type="submit"
          size="md"
          :is-loading="isSaving"
          :disabled="isSaving || isRegenerating || !modalAccountActive"
          @click="validationAttempted = true"
        >
          <template v-if="isSaving">
            {{ $t('PIPELINE_WEBHOOK.FORM.SAVING') }}
          </template>
          <template v-else>{{ $t('PIPELINE_WEBHOOK.FORM.SAVE') }}</template>
        </Button>
      </div>
    </form>
  </Modal>

  <ConfirmModal
    ref="discardConfirmation"
    :title="$t('PIPELINE_WEBHOOK.FORM.DISCARD_TITLE')"
    :description="$t('PIPELINE_WEBHOOK.FORM.DISCARD_DESCRIPTION')"
    :confirm-label="$t('PIPELINE_WEBHOOK.FORM.DISCARD_CONFIRM')"
    :cancel-label="$t('PIPELINE_WEBHOOK.FORM.KEEP_EDITING')"
  />

  <ConfirmModal
    ref="regenerateConfirmation"
    :title="$t('PIPELINE_WEBHOOK.FORM.REGENERATE_TITLE')"
    :description="$t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_CONFIRM')"
    :confirm-label="$t('PIPELINE_WEBHOOK.FORM.REGENERATE_SECRET')"
    :cancel-label="$t('PIPELINE_WEBHOOK.FORM.CANCEL')"
  />
</template>
